import { BLOCK_TYPES, getAvailabilityKind, normalizeBlockType } from '@/types/availability';
import type { AvailabilityEntry } from '@/types/availability';

export function isAllDayAvailabilityEntry(entry: AvailabilityEntry): boolean {
	return entry.allDay ?? (!entry.startTime && !entry.endTime);
}

/** Calendar presentation only; explicit blocks do not imply availability outside them. */
export function getAvailabilityRestrictions(entries: AvailabilityEntry[]) {
	const blocks = entries.filter((entry) => getAvailabilityKind(entry.blockType) === 'blocked');
	const fullyBlocked = blocks.some((entry) => normalizeBlockType(entry.blockType) === BLOCK_TYPES.BLOCK_GLOBAL && isAllDayAvailabilityEntry(entry));
	const timeRestricted = !fullyBlocked && blocks.some((entry) => !isAllDayAvailabilityEntry(entry));
	const groupRestricted = !fullyBlocked && blocks.some((entry) => normalizeBlockType(entry.blockType) === BLOCK_TYPES.BLOCK_GROUP);
	const clientRestricted = !fullyBlocked && blocks.some((entry) => normalizeBlockType(entry.blockType) === BLOCK_TYPES.BLOCK_CLIENT);
	const scopeLabel = groupRestricted && clientRestricted ? 'Group / client restricted' : clientRestricted ? 'Client restricted' : 'Group restricted';
	const labels = [timeRestricted ? 'Time restricted' : '', groupRestricted || clientRestricted ? scopeLabel : ''].filter(Boolean);

	return { blocks, fullyBlocked, timeRestricted, scopeRestricted: groupRestricted || clientRestricted, scopeLabel, labels };
}

/** Describes offered availability, independently of blocks and assigned matches. */
export function getAvailabilityCoverage(entries: AvailabilityEntry[]) {
	const available = entries.filter((entry) => getAvailabilityKind(entry.blockType) === 'available');
	const fullyAvailable = available.some((entry) => normalizeBlockType(entry.blockType) === BLOCK_TYPES.AVAILABLE_GLOBAL && isAllDayAvailabilityEntry(entry));
	const timeLimited = !fullyAvailable && available.some((entry) => !isAllDayAvailabilityEntry(entry));
	const groupLimited = !fullyAvailable && available.some((entry) => normalizeBlockType(entry.blockType) === BLOCK_TYPES.AVAILABLE_GROUP);
	const clientLimited = !fullyAvailable && available.some((entry) => normalizeBlockType(entry.blockType) === BLOCK_TYPES.AVAILABLE_CLIENT);
	const scopeLabel = groupLimited && clientLimited ? 'Available for selected groups / clients' : clientLimited ? 'Available for selected clients' : 'Available for selected groups';
	const labels = [timeLimited ? 'Available at specific times' : '', groupLimited || clientLimited ? scopeLabel : ''].filter(Boolean);

	return { available, fullyAvailable, timeLimited, scopeLimited: groupLimited || clientLimited, scopeLabel, labels };
}
